import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../api/axios";
import { loadStatus } from "../util";

export const getUsers = createAsyncThunk(
  "users/getUsers",
  async (reqData, { rejectWithValue, getState }) => {
    try {
      const state = getState().users;
      if (state.count.getUsers <= 1) {
        const res = await axios.get("/users");
        return res.data;
      }
      return { data: state.data, count: state.total };
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const getCommunityUsers = createAsyncThunk(
  "communities/getCommunityUsers",
  async (reqData, { rejectWithValue, getState }) => {
    try {
      const state = getState().users;
      if (state.count.getCommunityUsers <= 1) {
        const res = await axios.get("/community-users/" + reqData);
        return res.data;
      }
      return { data: state.data, count: state.total };
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const getOrgUsers = createAsyncThunk(
  "users/getOrgUsers",
  async (reqData, { rejectWithValue, getState }) => {
    try {
      const state = getState().users;
      if (state.count.getOrgUsers <= 1) {
        const res = await axios.get("/organization-users/" + reqData);
        return res.data;
      }
      return { data: state.data, count: state.total };
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const getDepartmentUsers = createAsyncThunk(
  "users/getDepartmentUsers",
  async (reqData, { rejectWithValue, getState }) => {
    try {
      const state = getState().users;
      if (state.count.getDepartmentUsers <= 1) {
        const res = await axios.get(
          "/department-users/" + reqData.id + "/" + reqData.slug
        );
        return res.data;
      }
      return { data: state.data, count: state.total };
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const getUser = createAsyncThunk(
  "users/getUser",
  async (reqData, { rejectWithValue, getState }) => {
    try {
      const state = getState().users;
      if (state.count.getUser <= 1) {
        const res = await axios.get("/user/" + reqData);
        return res.data;
      }
      return state.selected;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const addUser = createAsyncThunk(
  "users/addUser",
  async (reqData, { rejectWithValue }) => {
    try {
      const res = await axios.post("/add-user", reqData);
      return res.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const inviteUser = createAsyncThunk(
  "users/inviteUser",
  async (reqData, { rejectWithValue }) => {
    try {
      const res = await axios.post("/invite-user", reqData);
      return res.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const editUser = createAsyncThunk(
  "users/editUser",
  async ({ id, reqData }, { rejectWithValue }) => {
    try {
      const res = await axios.put("/edit-user/" + id, reqData);
      return res.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const deleteUser = createAsyncThunk(
  "users/deleteUser",
  async ({ id, reqData }, { rejectWithValue }) => {
    try {
      const res = await axios.delete("/delete-user/" + id, { params: reqData });
      return res.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

const usersSlice = createSlice({
  name: "users",
  initialState: {
    total: 0,
    data: [],
    selected: {},
    loading: false,
    error: null,
    load: {
      getUsers: false,
      getUser: false,
      getOrgUsers: false,
      getCommunityUsers: false,
      addUser: false,
      editUser: false,
      inviteUser: false,
      deleteUser: false,
      getDepartmentUsers: false,
    },
    count: {
      getUsers: 0,
      getUser: 0,
      getOrgUsers: 0,
      getCommunityUsers: 0,
      getDepartmentUsers: 0,
    },
  },
  reducers: {
    setUsers: (state, action) => {
      state.data = action.payload;
    },
    setSelectedUser: (state, action) => {
      state.selected = action.payload;
    },
    unsetUsers: (state) => {
      state.data = [];
      state.selected = {};
    },
  },
  extraReducers: (builder) => {
    builder
      //Get all Users
      .addCase(getUsers.pending, (state) => {
        state.load.getUsers = loadStatus.loading;
        state.error = null;
        state.count.getUsers = ++state.count.getUsers;
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        state.load.getUsers = loadStatus.success;
        state.data = action.payload.data;
        state.total = action.payload.count;
        state.count.getUsers = 0;
      })
      .addCase(getUsers.rejected, (state, action) => {
        state.load.getUsers = loadStatus.error;
        state.error = action.error;
        state.count.getUsers = 0;
      })
      //Get all Organization Users
      .addCase(getOrgUsers.pending, (state) => {
        state.load.getOrgUsers = loadStatus.loading;
        state.error = null;
        state.count.getOrgUsers = ++state.count.getOrgUsers;
      })
      .addCase(getOrgUsers.fulfilled, (state, action) => {
        state.load.getOrgUsers = loadStatus.success;
        state.data = action.payload.data;
        state.total = action.payload.count;
        state.count.getOrgUsers = 0;
      })
      .addCase(getOrgUsers.rejected, (state, action) => {
        state.load.getOrgUsers = loadStatus.error;
        state.error = action.error;
        state.count.getOrgUsers = 0;
      })
      //Get all Department Users
      .addCase(getDepartmentUsers.pending, (state) => {
        state.load.getDepartmentUsers = loadStatus.loading;
        state.error = null;
        state.count.getDepartmentUsers = ++state.count.getDepartmentUsers;
      })
      .addCase(getDepartmentUsers.fulfilled, (state, action) => {
        state.load.getDepartmentUsers = loadStatus.success;
        state.data = action.payload.data;
        state.total = action.payload.count;
        state.count.getDepartmentUsers = 0;
      })
      .addCase(getDepartmentUsers.rejected, (state, action) => {
        state.load.getDepartmentUsers = loadStatus.error;
        state.error = action.error;
        state.count.getDepartmentUsers = 0;
      })
      //Get all Community Users
      .addCase(getCommunityUsers.pending, (state) => {
        state.load.getCommunityUsers = loadStatus.loading;
        state.error = null;
        state.count.getCommunityUsers = ++state.count.getCommunityUsers;
      })
      .addCase(getCommunityUsers.fulfilled, (state, action) => {
        state.load.getCommunityUsers = loadStatus.success;
        state.data = action.payload.data;
        state.total = action.payload.count;
        state.count.getCommunityUsers = 0;
      })
      .addCase(getCommunityUsers.rejected, (state, action) => {
        state.load.getCommunityUsers = loadStatus.error;
        state.error = action.error;
        state.count.getCommunityUsers = 0;
      })
      //Get User
      .addCase(getUser.pending, (state) => {
        state.load.getUser = loadStatus.loading;
        state.error = null;
        state.count.getUser = ++state.count.getUser;
      })
      .addCase(getUser.fulfilled, (state, action) => {
        state.load.getUser = loadStatus.success;
        state.selected = action.payload;
        state.count.getUser = 0;
      })
      .addCase(getUser.rejected, (state, action) => {
        state.load.getUser = loadStatus.error;
        state.error = action.error;
        state.count.getUser = 0;
      })
      //Add User
      .addCase(addUser.pending, (state) => {
        state.load.addUser = loadStatus.loading;
        state.error = null;
      })
      .addCase(addUser.fulfilled, (state, action) => {
        state.load.addUser = loadStatus.success;
        state.data.unshift(action.payload);
      })
      .addCase(addUser.rejected, (state, action) => {
        state.load.addUser = loadStatus.error;
        state.error = action.error;
      })
      //nvite User
      .addCase(inviteUser.pending, (state) => {
        state.load.inviteUser = loadStatus.loading;
        state.error = null;
      })
      .addCase(inviteUser.fulfilled, (state, action) => {
        state.load.inviteUser = loadStatus.success;
      })
      .addCase(inviteUser.rejected, (state, action) => {
        state.load.inviteUser = loadStatus.error;
        state.error = action.error;
      })
      //Update User
      .addCase(editUser.pending, (state) => {
        state.load.editUser = loadStatus.loading;
        state.error = null;
      })
      .addCase(editUser.fulfilled, (state, action) => {
        state.load.editUser = loadStatus.success;
        const index = state.data.findIndex(
          (item) => (item.id = action.payload.id)
        );
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(editUser.rejected, (state, action) => {
        state.load.editUser = loadStatus.error;
        state.error = action.error;
      })
      //Delete User
      .addCase(deleteUser.pending, (state) => {
        state.load.deleteUser = loadStatus.loading;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.load.deleteUser = loadStatus.success;
        state.data.filter((item) => item.id !== action.meta.arg.id);
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.load.deleteUser = loadStatus.error;
        state.error = action.error;
      });
  },
});

export const { setUsers, setSelectedUser, unsetUsers } = usersSlice.actions;
export default usersSlice.reducer;
